import { useNavigate } from 'react-router-dom'
import { Badge, Button, TextLink } from '@/components/ui'
import type { BadgeProps } from '@/components/ui'
import { useStartConsultation } from '@/hooks/consultations/use-start-consultation'
import type { AppointmentWithDetails } from '@rezeta/shared'
import { formatTime, minutesUntil, statusBadgeVariant, statusLabel } from './helpers'
import { dashboardStrings } from './strings'

export interface UpcomingRowProps {
  appt: AppointmentWithDetails
  isFirst: boolean
}

export function UpcomingRow({ appt, isFirst }: UpcomingRowProps): JSX.Element {
  const navigate = useNavigate()
  const { start, isStarting } = useStartConsultation()

  const canStart = appt.status === 'scheduled' && !appt.consultationId
  const canContinue = appt.status === 'in_progress' && appt.consultationStatus === 'open'
  const actionLabel = canStart
    ? dashboardStrings.upcomingRowStart
    : canContinue
      ? dashboardStrings.upcomingRowContinue
      : null
  const initials = appt.patientName
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0] ?? '')
    .join('')
    .toUpperCase()

  const isPending = appt.status === 'scheduled'
  const isCompleted = appt.status === 'completed'

  let badgeVariant: BadgeProps['variant'] = 'draft'
  let badgeLabel = statusLabel(appt.status)

  if (isPending) {
    const mins = minutesUntil(appt.startsAt)
    if (mins >= 0 && mins <= 30) {
      badgeVariant = 'active'
      badgeLabel = dashboardStrings.upcomingRowPending
    } else {
      badgeVariant = 'draft'
    }
  } else {
    badgeVariant = statusBadgeVariant(appt.status)
  }

  const goToAgenda = (): void => void navigate('/agenda')

  return (
    // Rendered as a div (asChild) rather than a native button so the trailing
    // TextLink action button can nest without producing invalid button-in-button
    // markup. Keyboard accessibility is preserved via role/tabIndex/onKeyDown.
    <Button
      asChild
      variant="item"
      size="sm"
      className={[
        'relative flex items-center gap-4 w-full h-auto text-left py-2.5 pl-3.5 pr-4',
        'border-b border-n-100 last:border-b-0',
        'before:absolute before:left-0 before:top-3 before:bottom-3 before:w-0.5 before:bg-p-500',
        !isFirst && isCompleted ? 'opacity-70' : '',
      ].join(' ')}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={goToAgenda}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            goToAgenda()
          }
        }}
      >
      <div className="w-btn-sm h-btn-sm rounded-full bg-p-50 text-p-700 text-2xs font-semibold flex items-center justify-center shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-n-900 truncate">{appt.patientName}</div>
        {appt.reason && <div className="text-xs text-n-500 truncate mt-1">{appt.reason}</div>}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="font-mono text-xs text-n-500">{formatTime(appt.startsAt)}</span>
        <Badge variant={badgeVariant} showDot>
          {badgeLabel}
        </Badge>
        {actionLabel && (
          <TextLink
            tone="primary"
            size="md"
            disabled={isStarting}
            onClick={(e) => {
              e.stopPropagation()
              start(appt)
            }}
          >
            {actionLabel}
          </TextLink>
        )}
      </div>
      </div>
    </Button>
  )
}
